import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Sun, Sunset, ArrowRight, Waves, ShieldCheck, MapPin, Ticket, CalendarDays, Menu as MenuIcon, X } from "lucide-react";
import { api, imgUrl, formatPrice } from "@/lib/api";
import { ReservationDialog } from "@/components/ReservationDialog";

const NAV = [
  { id: "cartas", label: "Cartas" },
  { id: "eventos", label: "Eventos" },
  { id: "galeria", label: "Galería" },
];

const CartaCard = ({ slug, title, subtitle, image, icon: Icon, onClick, delay }) => (
  <motion.button
    data-testid={`carta-card-${slug}`} onClick={onClick}
    initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay }}
    className="group relative h-72 flex-1 overflow-hidden rounded-3xl text-left md:h-96">
    <div className="absolute inset-0 bg-cover bg-center transition-transform duration-[1200ms] group-hover:scale-110" style={{ backgroundImage: `url(${imgUrl(image)})` }} />
    <div className="absolute inset-0 hero-overlay" />
    <div className="relative z-10 flex h-full flex-col justify-end p-7 md:p-9">
      <span className="mb-3 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-white/15 ring-1 ring-white/25 backdrop-blur-md"><Icon className="h-6 w-6 text-white" /></span>
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#E0F2FE]">{subtitle}</p>
      <h3 className="font-display mt-1 text-3xl font-bold text-white md:text-4xl">{title}</h3>
      <span className="mt-4 inline-flex w-fit items-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-bold text-[#075985] transition-all group-hover:gap-3 group-hover:bg-[#EA580C] group-hover:text-white">Ver carta <ArrowRight className="h-4 w-4" /></span>
    </div>
  </motion.button>
);

const EventCard = ({ ev, onReserve, delay }) => (
  <motion.div
    data-testid={`event-card-${ev.id}`}
    initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay }}
    className="overflow-hidden rounded-3xl bg-white ring-1 ring-[#E7E5E4]">
    <div className="relative h-52 bg-secondary">
      {ev.image ? <img src={imgUrl(ev.image)} alt={ev.name} className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center text-[#D6D3D1]"><Ticket className="h-10 w-10" /></div>}
      {ev.sold_out ? <span className="absolute right-3 top-3 rounded-full bg-[#DC2626] px-3 py-1 text-xs font-bold text-white">Agotado</span>
        : <span className="absolute right-3 top-3 rounded-full bg-white/90 px-3 py-1 text-xs font-bold text-[#16A34A] backdrop-blur">{ev.available} disponibles</span>}
    </div>
    <div className="p-6">
      {ev.event_date ? <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-[#EA580C]"><CalendarDays className="h-3.5 w-3.5" /> {ev.event_date}</p> : null}
      <h3 className="font-display mt-1 text-2xl font-bold text-[#1C1917]">{ev.name}</h3>
      {ev.description ? <p className="mt-2 text-sm leading-relaxed text-[#57534E]">{ev.description}</p> : null}
      <div className="mt-5 flex items-center justify-between">
        {ev.price ? <span className="text-lg font-bold text-[#0369A1]">{formatPrice(ev.price)}</span> : <span className="text-sm font-semibold text-[#16A34A]">Entrada libre</span>}
        <button data-testid={`reserve-button-${ev.id}`} disabled={ev.sold_out} onClick={() => onReserve(ev)}
          className="rounded-full bg-[#EA580C] px-6 py-2.5 text-sm font-bold text-white transition-colors hover:bg-[#C2410C] disabled:cursor-not-allowed disabled:bg-[#D6D3D1]">
          {ev.sold_out ? "Agotado" : "Reservar"}
        </button>
      </div>
    </div>
  </motion.div>
);

const SectionTitle = ({ pre, title }) => (
  <div className="mb-10 text-center">
    {pre ? <p className="text-sm font-semibold uppercase tracking-[0.25em] text-[#EA580C]">{pre}</p> : null}
    <h2 className="font-display mt-2 text-3xl font-bold text-[#1C1917] md:text-5xl">{title}</h2>
  </div>
);

export default function Landing() {
  const navigate = useNavigate();
  const [s, setS] = useState({});
  const [events, setEvents] = useState([]);
  const [resEvent, setResEvent] = useState(null);
  const [navOpen, setNavOpen] = useState(false);

  const load = () => {
    api.get("/settings").then((r) => setS(r.data)).catch(() => {});
    api.get("/events").then((r) => setEvents(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const gallery = s.gallery || [];

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* NAV */}
      <header className="fixed top-0 left-0 right-0 z-40 border-b border-white/10 bg-[#075985]/70 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-3.5">
          <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} data-testid="brand-logo" className="flex items-center gap-2 text-white">
            {(s.logo_light || s.logo) ? (
              <img src={imgUrl(s.logo_light || s.logo)} alt={s.restaurant_name || "Salitre"} className="h-11 w-auto object-contain md:h-12" />
            ) : (
              <><Waves className="h-5 w-5" /><span className="font-display text-lg font-bold">{s.restaurant_name || "Chiringuito Salitre"}</span></>
            )}
          </button>
          <nav className="hidden items-center gap-1 md:flex">
            {NAV.map((n) => <a key={n.id} href={`#${n.id}`} className="rounded-full px-4 py-2 text-sm font-semibold text-white/85 transition-colors hover:bg-white/15 hover:text-white">{n.label}</a>)}
          </nav>
          <button data-testid="mobile-nav-toggle" onClick={() => setNavOpen((v) => !v)} className="rounded-full bg-white/15 p-2 text-white md:hidden">{navOpen ? <X className="h-5 w-5" /> : <MenuIcon className="h-5 w-5" />}</button>
        </div>
        {navOpen && (
          <nav className="flex flex-col gap-1 border-t border-white/10 px-5 py-3 md:hidden">
            {NAV.map((n) => <a key={n.id} href={`#${n.id}`} onClick={() => setNavOpen(false)} className="rounded-lg px-4 py-2.5 text-sm font-semibold text-white/90 hover:bg-white/15">{n.label}</a>)}
          </nav>
        )}
      </header>

      {/* HERO */}
      <section className="relative flex min-h-[92vh] items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${imgUrl(s.hero_bg)})` }} />
        <div className="absolute inset-0 hero-overlay" />
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="relative z-10 max-w-3xl px-6 text-center">
          <h1 data-testid="hero-title" className="font-display text-5xl font-bold leading-[1.05] text-white md:text-7xl">{s.hero_title || "Chiringuito Salitre"}</h1>
          <p data-testid="hero-subtitle" className="mx-auto mt-5 max-w-xl text-lg text-[#E0F2FE] md:text-xl">{s.hero_subtitle}</p>
          <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
            <a href="#cartas" className="rounded-full bg-[#EA580C] px-8 py-3.5 font-bold text-white transition-colors hover:bg-[#C2410C]">Ver Cartas</a>
            <a href="#eventos" className="rounded-full bg-white/15 px-8 py-3.5 font-bold text-white ring-1 ring-white/30 backdrop-blur-md transition-colors hover:bg-white/25">Eventos</a>
          </div>
        </motion.div>
      </section>

      {/* CARTAS */}
      <section id="cartas" className="mx-auto max-w-6xl scroll-mt-20 px-5 py-20 md:py-28">
        <SectionTitle pre={s.cartas_section_text} title={s.cartas_section_title || "Nuestras Cartas"} />
        <div className="flex flex-col gap-5 md:flex-row">
          <CartaCard slug="mediodia" title={s.mediodia_title || "Carta del Mediodía"} subtitle="Almuerzos · 13:00 – 17:00" image={s.mediodia_hero} icon={Sun} delay={0.05} onClick={() => navigate("/carta/mediodia")} />
          <CartaCard slug="tardes" title={s.tardes_title || "Carta de las Tardes"} subtitle="Atardeceres · 17:00 – cierre" image={s.tardes_hero} icon={Sunset} delay={0.15} onClick={() => navigate("/carta/tardes")} />
        </div>
      </section>

      {/* EVENTOS */}
      <section id="eventos" className="scroll-mt-20 bg-[#F5F7F9] py-20 md:py-28">
        <div className="mx-auto max-w-6xl px-5">
          <SectionTitle pre={s.eventos_section_text} title={s.eventos_section_title || "Eventos & Reservas"} />
          {events.length === 0 ? (
            <div className="rounded-3xl border border-dashed border-[#D6D3D1] py-16 text-center text-[#78716C]">
              Aún no hay eventos publicados. ¡Vuelve pronto!
            </div>
          ) : (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {events.map((ev, i) => <EventCard key={ev.id} ev={ev} onReserve={setResEvent} delay={i * 0.05} />)}
            </div>
          )}
        </div>
      </section>

      {/* GALERIA */}
      {gallery.length > 0 && (
        <section id="galeria" className="mx-auto max-w-6xl scroll-mt-20 px-5 py-20 md:py-28">
          <SectionTitle pre="El ambiente" title={s.gallery_section_title || "El Chiringuito"} />
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-4">
            {gallery.map((g, i) => (
              <motion.div key={i} initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.04 }}
                className={`overflow-hidden rounded-2xl ${i % 5 === 0 ? "col-span-2 row-span-2 aspect-square" : "aspect-square"}`}>
                <img src={imgUrl(g)} alt="" className="h-full w-full object-cover transition-transform duration-700 hover:scale-105" />
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* NOSOTROS */}
      <section id="nosotros" className="scroll-mt-20 bg-[#075985] py-20 text-white md:py-28">
        <div className="mx-auto grid max-w-6xl items-center gap-10 px-5 md:grid-cols-2">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.25em] text-[#F5B27E]">Nuestra casa</p>
            <h2 className="font-display mt-2 text-3xl font-bold md:text-5xl">{s.about_title || "Sobre Nosotros"}</h2>
            <p className="mt-5 text-lg leading-relaxed text-[#D6ECF7]">{s.about_text}</p>
          </div>
          <div className="h-72 overflow-hidden rounded-3xl md:h-96">
            <img src={imgUrl(gallery[0] || s.tardes_hero)} alt="" className="h-full w-full object-cover" />
          </div>
        </div>
      </section>

      {/* UBICACION */}
      <section id="ubicacion" className="mx-auto max-w-6xl scroll-mt-20 px-5 py-20 md:py-28">
        <SectionTitle pre="Cómo llegar" title={s.location_title || "Dónde Estamos"} />
        <div className="grid gap-8 md:grid-cols-2">
          <div className="flex flex-col justify-center">
            <p className="text-lg leading-relaxed text-[#57534E]">{s.location_text}</p>
            <div className="mt-5 flex items-start gap-3 rounded-2xl bg-secondary p-5">
              <MapPin className="mt-0.5 h-6 w-6 shrink-0 text-[#EA580C]" />
              <span className="font-semibold text-[#1C1917]">{s.location_address}</span>
            </div>
          </div>
          <div className="h-72 overflow-hidden rounded-3xl md:h-80">
            {s.location_map_url ? (
              <iframe title="mapa" src={s.location_map_url} className="h-full w-full border-0" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
            ) : (
              <div className="flex h-full items-center justify-center bg-[#E0F2FE] text-[#075985]"><MapPin className="mr-2 h-6 w-6" /> Mapa disponible próximamente</div>
            )}
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-[#E7E5E4] bg-white py-10">
        <div className="mx-auto flex max-w-6xl flex-col items-center gap-4 px-5 text-center">
          {s.logo ? <img src={imgUrl(s.logo)} alt={s.restaurant_name} className="h-16 w-auto object-contain" /> : <div className="flex items-center gap-2 text-[#075985]"><Waves className="h-5 w-5" /><span className="font-display text-lg font-bold">{s.restaurant_name || "Chiringuito Salitre"}</span></div>}
          <p className="text-sm text-[#78716C]">{s.tagline}</p>

          <nav className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-sm font-semibold text-[#57534E]">
            <button data-testid="footer-aviso-legal" onClick={() => navigate("/legal/aviso-legal")} className="hover:text-[#0369A1]">Aviso legal</button>
            <button data-testid="footer-privacidad" onClick={() => navigate("/legal/privacidad")} className="hover:text-[#0369A1]">Privacidad</button>
            <button data-testid="footer-cookies" onClick={() => navigate("/legal/cookies")} className="hover:text-[#0369A1]">Cookies</button>
            <button data-testid="footer-terminos" onClick={() => navigate("/legal/terminos")} className="hover:text-[#0369A1]">Términos y condiciones</button>
          </nav>

          <p className="text-xs text-[#78716C]">Pagos seguros con tarjeta a través de <b>Redsys</b> · Visa · Mastercard</p>
          <p className="max-w-xl text-xs font-semibold text-[#B45309]">Prohibida la venta de bebidas alcohólicas a menores de 18 años.</p>
          {(s.legal_name || s.legal_cif) ? (
            <p className="text-xs text-[#A8A29E]">{s.legal_name}{s.legal_cif ? ` · CIF ${s.legal_cif}` : ""}{s.legal_address ? ` · ${s.legal_address}` : ""}</p>
          ) : null}

          <button data-testid="admin-access-link" onClick={() => navigate("/admin")} className="mt-1 inline-flex items-center gap-2 text-sm font-medium text-[#A8A29E] transition-colors hover:text-[#0369A1]"><ShieldCheck className="h-4 w-4" /> Acceso administrador</button>
        </div>
      </footer>

      <ReservationDialog event={resEvent} open={!!resEvent} onClose={() => setResEvent(null)} onDone={load} />
    </div>
  );
}
